import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Breadcrumbs,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

import { useLocation, useNavigate } from "react-router-dom";
import { HeaderProps } from "../../types/interface";
import { COLORS } from "../../theme/colors";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

import { getUnreadCount } from "../../api/api";

const Header: React.FC<HeaderProps> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const user = useSelector((state: RootState) => state.auth.user);

  const [count, setCount] = useState(0);

  // ================= PAGE LOGIC =================
  const getPageData = () => {
    if (path.startsWith("/leads/create")) {
      return { title: "Create Lead", parent: "Leads" };
    }

    if (path.startsWith("/leads/edit")) {
      return { title: "Edit Lead", parent: "Leads" };
    }

    if (path.startsWith("/leads/view")) {
      return { title: "View Lead", parent: "Leads" };
    }

    if (path.startsWith("/leads")) {
      return { title: "Leads", parent: null };
    }

    if (path.startsWith("/credit/create")) {
      return {
        title: "Create Credit",
        parent: "Credit",
      };
    }

    if (path.startsWith("/credit/view")) {
      return {
        title: "View Credit",
        parent: "Credit",
      };
    }

    if (path.startsWith("/credit")) {
      return {
        title: "Credit",
        parent: null,
      };
    }

    if (path.startsWith("/loans")) {
      return { title: "Loans", parent: null };
    }

    if (path.startsWith("/commission")) {
      return { title: "Commission", parent: null };
    }

    if (path.startsWith("/disbursement/create")) {
      return {
        title: "Create Disbursement",
        parent: "Disbursement",
      };
    }

    if (path.startsWith("/disbursement/view")) {
      return {
        title: "View Disbursement",
        parent: "Disbursement",
      };
    }

    if (path.startsWith("/disbursement")) {
      return {
        title: "Disbursement",
        parent: null,
      };
    }

    if (path.startsWith("/notifications")) {
      return { title: "Notifications", parent: null };
    }

    return { title: "Dashboard", parent: null };
  };

  const { title, parent } = getPageData();

  // ================= FETCH COUNT =================
  const fetchCount = async () => {
    try {
      const res = await getUnreadCount();
      setCount(res.data?.count || 0);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchCount();

    // 🔥 optional auto refresh every 10 sec
    const interval = setInterval(fetchCount, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        height: "64px",
        px: 2,
        backgroundColor: COLORS.cardBg,
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      {/* LEFT */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton onClick={() => setCollapsed(!collapsed)}>
          <MenuIcon />
        </IconButton>

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>

          {parent && (
            <Breadcrumbs
              separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
              sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}
            >
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                {parent}
              </Typography>

              <Typography
                sx={{
                  fontSize: 11,
                  color: COLORS.primary,
                  fontWeight: 500,
                }}
              >
                {title}
              </Typography>
            </Breadcrumbs>
          )}
        </Box>
      </Box>

      {/* RIGHT */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* 🔔 NOTIFICATION */}
        <IconButton onClick={() => navigate("/notifications")}>
          <Badge badgeContent={count} color="error">
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>

        {/* USER */}
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Hi, {user?.name || user?.role || "User"}
        </Typography>

        {/* AVATAR */}
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 14,
            bgcolor: COLORS.primary,
            cursor: "pointer",
          }}
        >
          {(user?.name || user?.role || "U")[0].toUpperCase()}
        </Avatar>
      </Box>
    </Box>
  );
};

export default Header;
